import {
  faBan,
  faCheckCircle,
  faExclamationTriangle,
  faMinusCircle,
  faPenFancy,
  faPlusCircle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MemreButton, MemreFlexBox, MemreText } from 'components/core';
import * as Color from 'core/colors';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useUserCsvUploads } from 'hooks/useUserCsvUploads';
import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

dayjs.extend(relativeTime);

export const CsvUploadsStatus: React.FC = () => {
  const { data, isLoading, isError, refetch } = useUserCsvUploads();
  const [expanded, setExpanded] = useState(false);

  // If still loading or error, suppress this banner
  if (isLoading || isError || !data?.data) {
    return null;
  }

  // Only show uploads created within the last 24 hours
  const allUploads = data.data;
  const twentyFourHoursAgo = dayjs().subtract(24, 'hour');
  const recentUploads = allUploads.filter((upload: any) => {
    const createdAt = dayjs(upload.attributes['created-at']);
    // Filter out parse_completed (dry runs)
    const state = upload.attributes.state;
    if (state === 'parse_completed') return false;
    return createdAt.isAfter(twentyFourHoursAgo);
  });

  // If none are “recent,” hide the banner
  if (!recentUploads.length) {
    return null;
  }

  // Show up to 3 items unless expanded
  const itemsToShow = expanded ? recentUploads : recentUploads.slice(0, 3);

  return (
    <Container>
      <MemreFlexBox direction="row" justify="space-between" align="center">
        <TitleGroup>
          <MemreText variant="h5" sx={{ margin: 0 }}>
            Recent CSV Uploads
          </MemreText>
          <MemreText variant="subtitle2" sx={{ color: '#666' }}>
            From the last 24 hours
          </MemreText>
        </TitleGroup>
        <MemreButton text="Refresh" variant="contained" color="secondary" onClick={() => refetch()} size="small" />
      </MemreFlexBox>

      <UploadsList>
        {itemsToShow.map((upload: any) => {
          const { id } = upload;
          const { state, 'created-at': createdAt } = upload.attributes;
          const meta = upload.meta || {};
          const parsedCount = meta['parsed-count'];

          return (
            <UploadCard key={id}>
              <CardRow>
                <StateBar state={state} />
                <CardContent>
                  <SingleLine>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <CardTitle>ID: {id}</CardTitle>
                      <CreatedAt>{dayjs(createdAt).fromNow()}</CreatedAt>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      {parsedCount && (
                        <StatsRow>
                          <StatPill>
                            <FontAwesomeIcon icon={faPlusCircle} />
                            <StatLabel>Added:</StatLabel>
                            <StatValue>{parsedCount.added ?? 0}</StatValue>
                          </StatPill>
                          <StatPill>
                            <FontAwesomeIcon icon={faPenFancy} />
                            <StatLabel>Modified:</StatLabel>
                            <StatValue>{parsedCount.modified ?? 0}</StatValue>
                          </StatPill>
                          <StatPill>
                            <FontAwesomeIcon icon={faBan} />
                            <StatLabel>Omitted:</StatLabel>
                            <StatValue>{parsedCount.omitted ?? 0}</StatValue>
                          </StatPill>
                          <StatPill>
                            <FontAwesomeIcon icon={faMinusCircle} />
                            <StatLabel>Removed:</StatLabel>
                            <StatValue>{parsedCount.removed ?? 0}</StatValue>
                          </StatPill>
                        </StatsRow>
                      )}

                      <IconWrapper>{renderStateIcon(state)}</IconWrapper>
                    </div>
                  </SingleLine>
                </CardContent>
              </CardRow>
            </UploadCard>
          );
        })}
      </UploadsList>

      {recentUploads.length > 3 && (
        <ShowMoreWrapper>
          <MemreButton
            text={expanded ? 'Show Less' : 'Show More'}
            variant="outlined"
            color="secondary"
            onClick={() => setExpanded(!expanded)}
            size="small"
          />
        </ShowMoreWrapper>
      )}
    </Container>
  );
};

/**
 * Renders a simple icon to represent the final states we might see.
 */
function renderStateIcon(state: string) {
  switch (state) {
    case 'persist_completed':
      return <FontAwesomeIcon icon={faCheckCircle} style={{ color: Color.ceregoGreen, fontSize: '22px' }} />;
    case 'unprocessed':
      return <FontAwesomeIcon icon={faSpinner} style={{ color: Color.reportOrange, fontSize: '22px' }} spin />;
    case 'error':
      return <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: Color.reportRed, fontSize: '22px' }} />;
    default:
      // For any other states not in the sample data:
      return <FontAwesomeIcon icon={faSpinner} style={{ color: Color.textGray, fontSize: '22px' }} />;
  }
}

/* ----------------- STYLES & ANIMATIONS ----------------- */

// A little fade-up animation for each card
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  background-color: ${Color.veryLightBlue};
  border: 1px solid ${Color.deepBlueGray};
  padding: 16px;
  margin-bottom: 24px;
  border-radius: 8px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const UploadsList = styled.div`
  margin-top: 16px;
`;

const UploadCard = styled.div`
  background-color: ${Color.white};
  border: 1px solid ${Color.veryLightGray};
  border-radius: 6px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  animation: ${fadeInUp} 0.3s ease both;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
`;

const CardRow = styled.div`
  display: flex;
`;

const StateBar = styled.div<{ state: string }>`
  width: 5px;
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  margin-right: 12px;
  background-color: ${({ state }) => {
    switch (state) {
      case 'persist_completed':
        return '#28a745'; // green
      case 'unprocessed':
        return '#ffc107'; // yellow
      case 'error':
        return '#dc3545'; // red
      default:
        return '#6c757d'; // grey
    }
  }};
`;

const CardContent = styled.div`
  flex: 1;
  padding: 10px 8px;
`;

const SingleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const CardTitle = styled(MemreText).attrs({ variant: 'subtitle1' })`
  margin: 0;
  font-weight: 600;
`;

const CreatedAt = styled.span`
  margin-left: 1rem;
  font-size: 0.85rem;
  color: #666;
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: ${Color.veryLightGray};
  border-radius: 16px;
  padding: 6px 10px;
  border: 1px solid ${Color.veryLightGray};
  font-size: 0.85rem;
  color: ${Color.darkGray};
`;

const StatLabel = styled.span`
  font-weight: 500;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: #333;
`;

const IconWrapper = styled.div`
  margin-left: 1rem;
`;

const ShowMoreWrapper = styled.div`
  margin-top: 8px;
`;
